import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { COOKIE_CONSENT_EVENT, clearMetrikaStorage, readCookieConsent } from "@/lib/cookieConsent";

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void;
  }
}

const COUNTER_ID = 109037743;
const SCRIPT_ID = "yandex-metrika-script";
let initialized = false;

const initMetrika = () => {
  if (readCookieConsent() !== "analytics" || initialized || typeof window.ym !== "function") return;
  window.ym(COUNTER_ID, "init", { ssr: true, webvisor: true, clickmap: true, ecommerce: "dataLayer", referrer: document.referrer, url: location.href, accurateTrackBounce: true, trackLinks: true });
  initialized = true;
};

const enableMetrika = () => {
  if (typeof window === "undefined") return;
  if (window.ym) return initMetrika();
  window.ym = (...args: unknown[]) => {
    const queue = window.ym as typeof window.ym & { a?: unknown[][]; l?: number };
    queue.a = queue.a || [];
    queue.a.push(args);
  };
  (window.ym as typeof window.ym & { l?: number }).l = Date.now();
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = "https://mc.yandex.ru/metrika/tag.js?id=109037743";
  script.onload = initMetrika;
  document.head.appendChild(script);
};

const disableMetrika = () => {
  const script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (script) {
    script.onload = null;
    script.remove();
  }
  if (initialized && window.ym) window.ym(COUNTER_ID, "destruct");
  initialized = false;
  clearMetrikaStorage();
};

const YandexMetrika = () => {
  const location = useLocation();

  useEffect(() => {
    const protectForms = (root: ParentNode = document) => {
      root.querySelectorAll("input, textarea, select").forEach((field) => field.classList.add("ym-disable-keys"));
      root.querySelectorAll("form").forEach((form) => form.classList.add("ym-disable-submit"));
    };
    protectForms();
    const observer = new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) protectForms(node);
    })));
    observer.observe(document.body, { childList: true, subtree: true });
    if (readCookieConsent() === "analytics") enableMetrika();
    const onChoice = (event: Event) => {
      const value = (event as CustomEvent<{ value: string }>).detail.value;
      if (value === "analytics") enableMetrika(); else disableMetrika();
    };
    window.addEventListener(COOKIE_CONSENT_EVENT, onChoice);
    return () => {
      observer.disconnect();
      window.removeEventListener(COOKIE_CONSENT_EVENT, onChoice);
    };
  }, []);

  useEffect(() => {
    if (!initialized || typeof window.ym !== "function") return;
    const url = location.pathname + location.search + location.hash;
    window.ym(COUNTER_ID, "hit", url);
  }, [location]);

  return null;
};

export default YandexMetrika;
